/**
 * RoutePlanningMap - Mapa para planejamento de rotas
 * Exibe entregas, veiculos e rotas otimizadas
 */

import { useEffect, useId } from 'react';
import maplibregl from 'maplibre-gl';
import { map } from '../../map/core/MapView';
import { polylineToGeoJSON } from '../../common/services/routingService';
import DraggableMarkerLayer from './DraggableMarkerLayer';
import { LEG_COLORS, getRouteColor } from '../utils/colorUtils';

const ROUTE_COLORS = [
  '#00d4aa', // teal
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#10b981', // emerald
  '#3b82f6', // blue
  '#ec4899', // pink
  '#6366f1', // indigo
];

const RoutePlanningMap = ({
  deliveries = [],
  vehicles = [],
  optimizedRoutes = [],
  selectedRouteIndex = null,
  onDeliveryClick,
  onDeliveryDrag,
  onVehicleClick,
  onMapClick,
}) => {
  const baseId = useId();

  const vehiclesSourceId = `${baseId}-vehicles`;
  const routesSourceId = `${baseId}-routes`;

  // Inicializar sources e layers
  // ORDEM IMPORTANTE: rotas primeiro (embaixo), depois veiculos (topo)
  // Entregas sao renderizadas via DraggableMarkerLayer (marcadores HTML)
  useEffect(() => {
    // 1. Source e Layer para ROTAS (camada mais baixa)
    if (!map.getSource(routesSourceId)) {
      map.addSource(routesSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      // Layer de linhas para rotas
      map.addLayer({
        id: `${routesSourceId}-lines`,
        source: routesSourceId,
        type: 'line',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['case', ['get', 'selected'], 6, 4],
          'line-opacity': ['case', ['get', 'selected'], 1, 0.8],
        },
      });
    }

    // 2. Source e Layer para VEICULOS (camada do topo)
    if (!map.getSource(vehiclesSourceId)) {
      map.addSource(vehiclesSourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      // Layer de icones para veiculos
      map.addLayer({
        id: `${vehiclesSourceId}-icons`,
        source: vehiclesSourceId,
        type: 'circle',
        paint: {
          'circle-radius': 16,
          'circle-color': ['get', 'color'],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#fff',
        },
      });
    }

    // Cleanup
    return () => {
      // Remover layers
      if (map.getLayer(`${vehiclesSourceId}-icons`)) map.removeLayer(`${vehiclesSourceId}-icons`);
      if (map.getLayer(`${routesSourceId}-lines`)) map.removeLayer(`${routesSourceId}-lines`);

      // Remover sources
      if (map.getSource(vehiclesSourceId)) map.removeSource(vehiclesSourceId);
      if (map.getSource(routesSourceId)) map.removeSource(routesSourceId);
    };
  }, [baseId]);

  // Entregas sao renderizadas via DraggableMarkerLayer

  // Atualizar veiculos
  useEffect(() => {
    const source = map.getSource(vehiclesSourceId);
    if (!source) return;

    const features = vehicles
      .filter((v) => v.lat && v.lng)
      .map((v, index) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [v.lng, v.lat],
        },
        properties: {
          id: v.id,
          color: ROUTE_COLORS[index % ROUTE_COLORS.length],
          name: v.name,
        },
      }));

    source.setData({ type: 'FeatureCollection', features });
  }, [vehicles, vehiclesSourceId]);

  // Atualizar rotas otimizadas com segmentos coloridos por leg
  useEffect(() => {
    const source = map.getSource(routesSourceId);
    if (!source) return;

    const features = [];

    optimizedRoutes
      .filter((r) => r.geometry)
      .forEach((route, routeIndex) => {
        const isSelected = selectedRouteIndex === routeIndex;
        const routeColor = getRouteColor(routeIndex);

        // Verificar se temos legs com geometrias individuais
        const hasLegGeometries = route.legs?.some((leg) => leg.geometry);

        if (hasLegGeometries && route.legs.length > 1) {
          // Renderizar cada leg com cor distinta
          route.legs.forEach((leg, legIndex) => {
            if (!leg.geometry) return;

            const legGeometry = typeof leg.geometry === 'string'
              ? polylineToGeoJSON(leg.geometry)
              : leg.geometry;

            features.push({
              type: 'Feature',
              geometry: legGeometry,
              properties: {
                vehicleId: route.vehicleId,
                routeIndex,
                legIndex,
                color: LEG_COLORS[legIndex % LEG_COLORS.length],
                selected: isSelected,
                isLeg: true,
              },
            });
          });
        } else {
          // Renderizar rota inteira com cor unica
          // Tentar dividir a geometria baseado nos steps se temos coordenadas
          const geometry = typeof route.geometry === 'string'
            ? polylineToGeoJSON(route.geometry)
            : route.geometry;

          // Verificar se podemos dividir a rota usando coordenadas dos steps
          const jobSteps = route.steps?.filter((s) => s.type === 'job' && s.location) || [];

          if (jobSteps.length > 1 && geometry?.coordinates?.length > 10) {
            // Tentar dividir a geometria em segmentos
            const coords = geometry.coordinates;
            const segmentPoints = [0]; // Indices de divisao

            // Encontrar pontos de divisao baseado nos stops
            jobSteps.forEach((step) => {
              const stepLng = step.location.lng;
              const stepLat = step.location.lat;

              // Encontrar o ponto mais proximo na geometria
              let minDist = Infinity;
              let minIdx = 0;

              for (let i = segmentPoints[segmentPoints.length - 1]; i < coords.length; i += 1) {
                const [lng, lat] = coords[i];
                const dist = Math.abs(lng - stepLng) + Math.abs(lat - stepLat);
                if (dist < minDist) {
                  minDist = dist;
                  minIdx = i;
                }
              }

              if (minIdx > segmentPoints[segmentPoints.length - 1]) {
                segmentPoints.push(minIdx);
              }
            });

            // Criar features para cada segmento
            for (let i = 0; i < segmentPoints.length; i += 1) {
              const startIdx = segmentPoints[i];
              const endIdx = segmentPoints[i + 1] || coords.length - 1;

              if (endIdx > startIdx) {
                const segmentCoords = coords.slice(startIdx, endIdx + 1);

                features.push({
                  type: 'Feature',
                  geometry: {
                    type: 'LineString',
                    coordinates: segmentCoords,
                  },
                  properties: {
                    vehicleId: route.vehicleId,
                    routeIndex,
                    legIndex: i,
                    color: LEG_COLORS[i % LEG_COLORS.length],
                    selected: isSelected,
                    isLeg: true,
                  },
                });
              }
            }
          } else {
            // Fallback: rota inteira com cor unica
            features.push({
              type: 'Feature',
              geometry,
              properties: {
                vehicleId: route.vehicleId,
                routeIndex,
                color: routeColor,
                selected: isSelected,
                isLeg: false,
              },
            });
          }
        }
      });

    source.setData({ type: 'FeatureCollection', features });
  }, [optimizedRoutes, selectedRouteIndex, routesSourceId]);

  // Click handlers (entregas sao handled via DraggableMarkerLayer)
  useEffect(() => {
    const handleVehicleClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: [`${vehiclesSourceId}-icons`],
      });

      if (features.length > 0) {
        const vehicleId = features[0].properties.id;
        const vehicle = vehicles.find((v) => v.id === vehicleId);
        if (vehicle) {
          onVehicleClick?.(vehicle);
        }
      }
    };

    const handleMapClick = (e) => {
      // Verificar se clicou em vehicle
      let vehicleFeatures = [];

      try {
        if (map.getLayer(`${vehiclesSourceId}-icons`)) {
          vehicleFeatures = map.queryRenderedFeatures(e.point, {
            layers: [`${vehiclesSourceId}-icons`],
          });
        }
      } catch (error) {
        console.warn('[RoutePlanningMap] Error querying features:', error);
      }

      // Se não clicou em veiculo, adicionar entrega
      if (vehicleFeatures.length === 0) {
        onMapClick?.({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      }
    };

    // Cursor pointer para veiculos
    const handleMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    map.on('click', `${vehiclesSourceId}-icons`, handleVehicleClick);
    map.on('click', handleMapClick);
    map.on('mouseenter', `${vehiclesSourceId}-icons`, handleMouseEnter);
    map.on('mouseleave', `${vehiclesSourceId}-icons`, handleMouseLeave);

    return () => {
      map.off('click', `${vehiclesSourceId}-icons`, handleVehicleClick);
      map.off('click', handleMapClick);
      map.off('mouseenter', `${vehiclesSourceId}-icons`, handleMouseEnter);
      map.off('mouseleave', `${vehiclesSourceId}-icons`, handleMouseLeave);
    };
  }, [vehicles, onVehicleClick, onMapClick]);

  // Fit bounds quando tiver dados
  useEffect(() => {
    const allPoints = [
      ...deliveries.map((d) => [d.lng, d.lat]),
      ...vehicles.filter((v) => v.lat && v.lng).map((v) => [v.lng, v.lat]),
    ];

    if (allPoints.length > 1) {
      const bounds = allPoints.reduce(
        (b, [lng, lat]) => b.extend([lng, lat]),
        new maplibregl.LngLatBounds(allPoints[0], allPoints[0]),
      );

      map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 350, right: 50 },
        maxZoom: 14,
      });
    } else if (allPoints.length === 1) {
      map.flyTo({
        center: allPoints[0],
        zoom: 14,
      });
    }
  }, [deliveries.length, vehicles.length]);

  return (
    <DraggableMarkerLayer
      deliveries={deliveries}
      onDeliveryDrag={onDeliveryDrag}
      onDeliveryClick={onDeliveryClick}
    />
  );
};

export default RoutePlanningMap;
